import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, FilePlus2, FileText, Pencil, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { useEditorStore } from '../../stores/editor-store';
import { Button } from '../ui/Button';
import { appAlert, appConfirm } from '../../services/app-alerts';

interface PagesPanelProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

type GrapesPage = any;

function safeName(page: GrapesPage): string {
  return String(page?.get?.('name') ?? page?.get?.('id') ?? 'Page');
}

function safeId(page: GrapesPage): string {
  return String(page?.get?.('id') ?? page?.id ?? safeName(page));
}

export function PagesPanel({ collapsed = false, onToggle }: PagesPanelProps) {
  const editor = useEditorStore((s) => s.editorInstance) as any;
  const [pages, setPages] = useState<GrapesPage[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');

  const refresh = () => {
    if (!editor?.Pages?.getAll) {
      setPages([]);
      setSelectedId('');
      return;
    }
    const all = editor.Pages.getAll();
    const list = Array.isArray(all) ? all : all?.models ?? [];
    setPages(list);
    const selected = editor.Pages.getSelected?.();
    if (selected) setSelectedId(safeId(selected));
  };

  useEffect(() => {
    if (!editor) return;
    refresh();

    const handler = () => refresh();
    editor.on?.('page:add', handler);
    editor.on?.('page:remove', handler);
    editor.on?.('page:update', handler);
    editor.on?.('page:select', handler);
    editor.on?.('update', handler);

    return () => {
      editor.off?.('page:add', handler);
      editor.off?.('page:remove', handler);
      editor.off?.('page:update', handler);
      editor.off?.('page:select', handler);
      editor.off?.('update', handler);
    };
  }, [editor]);

  const items = useMemo(
    () =>
      pages.map((p) => ({
        id: safeId(p),
        name: safeName(p),
        page: p,
      })),
    [pages]
  );

  const startRename = (id: string, current: string) => {
    setEditingId(id);
    setDraftName(current);
  };

  const commitRename = () => {
    if (!editingId) return;
    const next = draftName.trim();
    if (!next) {
      setEditingId(null);
      return;
    }
    const entry = items.find((it) => it.id === editingId);
    if (entry?.page?.set) {
      entry.page.set('name', next);
    }
    setEditingId(null);
  };

  const handleAddPage = () => {
    if (!editor?.Pages?.add) return;
    const n = items.length + 1;
    const page = editor.Pages.add({ name: `Page ${n}` });
    if (page && editor.Pages.select) editor.Pages.select(page);
    refresh();
  };

  const handleDeletePage = async (id: string) => {
    if (!editor?.Pages) return;
    if (items.length <= 1) {
      await appAlert('You cannot delete the last page.', { title: 'Delete page' });
      return;
    }

    const entry = items.find((it) => it.id === id);
    if (!entry) return;

    const confirmed = await appConfirm(`Delete "${entry.name}"?`, {
      title: 'Delete page',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (!confirmed) return;

    try {
      if (typeof editor.Pages.remove === 'function') {
        editor.Pages.remove(entry.page);
      } else if (typeof entry.page.remove === 'function') {
        entry.page.remove();
      }
    } finally {
      refresh();
    }
  };

  if (!editor) {
    return (
      <div className="flex flex-col h-full">
        <div
          className={clsx(
            'py-3 border-b border-surface-lighter flex items-center',
            collapsed ? 'px-2 justify-center' : 'px-3 justify-between'
          )}
        >
          {!collapsed && <h3 className="text-sm font-medium text-text">Pages</h3>}
          {onToggle && (
            <button
              onClick={onToggle}
              className="text-text-muted hover:text-text transition-colors"
              title={collapsed ? 'Expand pages panel' : 'Collapse pages panel'}
            >
              {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          )}
        </div>
        {!collapsed && (
          <div className="p-4 text-xs text-text-muted">
            Open a project to manage its pages.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div
        className={clsx(
          'py-3 border-b border-surface-lighter flex items-center',
          collapsed ? 'px-2 justify-center' : 'px-3 justify-between'
        )}
      >
        {!collapsed && <h3 className="text-sm font-medium text-text">Pages</h3>}
        <div className={clsx('flex items-center gap-2', collapsed && 'flex-col')}>
          {!collapsed && (
            <Button size="sm" onClick={handleAddPage} title="Add page">
              <FilePlus2 size={14} />
            </Button>
          )}
          {onToggle && (
            <button
              onClick={onToggle}
              className="text-text-muted hover:text-text transition-colors"
              title={collapsed ? 'Expand pages panel' : 'Collapse pages panel'}
            >
              {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          )}
        </div>
      </div>

      {!collapsed && (
        <div className="p-2 flex-1 overflow-auto">
          {items.length === 0 ? (
            <div className="p-4 text-center">
              <p className="text-xs text-text-muted">No pages yet</p>
              <div className="mt-3 flex justify-center">
                <Button size="sm" onClick={handleAddPage}>
                  <FilePlus2 size={14} />
                  <span>Add page</span>
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {items.map((it) => {
                const active = it.id === selectedId;
                const editing = it.id === editingId;
                return (
                  <div
                    key={it.id}
                    className={clsx(
                      'group w-full flex items-center justify-between gap-2 p-2 rounded transition-colors',
                      active ? 'bg-accent/20 text-accent ring-1 ring-accent/60' : 'hover:bg-surface-lighter text-text'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (editor?.Pages?.select) editor.Pages.select(it.page);
                        setSelectedId(it.id);
                      }}
                      className="flex items-center gap-2 min-w-0 flex-1"
                    >
                      <FileText size={14} className="shrink-0" />
                      {editing ? (
                        <input
                          autoFocus
                          value={draftName}
                          onChange={(e) => setDraftName(e.target.value)}
                          onBlur={commitRename}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitRename();
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          className="w-full bg-surface px-2 py-1 rounded text-xs text-text border border-surface-lighter focus:outline-none focus:border-accent"
                        />
                      ) : (
                        <span className="text-xs truncate">{it.name}</span>
                      )}
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => startRename(it.id, it.name)}
                        className="p-1 opacity-0 group-hover:opacity-100 hover:text-text transition-opacity"
                        title="Rename"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeletePage(it.id)}
                        className="p-1 opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity"
                        title="Delete"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

